/** Global breadcrumb trail rendered above every routed page.
 *
 * Derived from the URL and the menu: `/storage/abc-123` → Home / Volumes /
 * <name>; `/sase/ai/apps` → Home / SASE / AI security / AI apps. The page is
 * the menu leaf the URL is under and is named as the menu names it — one
 * source, so a page in the menu always has a trail and the trail says what
 * the menu said (a static map used to drift: no trail on Queues or
 * Notifications, "Stacks" above the Blueprints page, `parameter-groups` read
 * as a database's id). The leaf prefers the resource's display name
 * (published by the detail page via `stores/breadcrumbStore` once loaded) and
 * falls back to a shortened id while the name is unknown. The crumb's job is
 * the way back, so the leaf is plain text and what is above it links.
 * Unknown sections render nothing.
 *
 * This is the only trail (platform test round 4). The layout's own
 * menu-derived one is off (`breadcrumbRender={false}` in `DashboardLayout`),
 * and a trail a page passes to its own header is not drawn (`index.css`):
 * the SASE and Private access pages each kept their own, which is how they
 * came to start at "SASE" with no Home, to differ from page to page, and to
 * be missing altogether on Cloud apps and on every page still loading.
 */

import type { ReactNode } from "react";
import { Breadcrumb } from "antd";
import { Link, useLocation } from "react-router-dom";

import { menuTrailFor, menuTrails } from "@/layouts/menuRoutes";
import { SASE_HOME, SASE_TITLE } from "@/lib/sectionBase";
import { useBreadcrumbStore } from "@/stores/breadcrumbStore";

/** Sections reached from the account menu or a link, not the sidebar. */
const OFF_MENU = [
  { path: "/fleet", name: "Fleet", sase: false, groups: [] },
  { path: "/account", name: "Account", sase: false, groups: [] },
];

const TRAILS = [...menuTrails(), ...OFF_MENU];

/** Pages under a menu page that are not one resource's page. */
const SUB_PAGES: Record<string, string> = {
  "get-started": "Get started",
  "sign-in-test": "Sign-in test",
  activity: "Activity",
  reports: "Reports",
  partner: "Partner",
  new: "New",
};

/** Path words that only say what kind of id follows (`/policies/rules/<id>`). */
const KIND_WORDS = new Set(["rules", "users", "apps"]);

/** `6f9619ff…` for long opaque ids (UUIDs); short human slugs render as-is. */
function shortId(id: string): string {
  return id.length > 13 ? `${id.slice(0, 8)}…` : id;
}

function Breadcrumbs({
  home = true,
}: {
  /** False where there is no cloud home to go back to (the *SASE only* view
   *  and the SASE edition): the trail then starts at SASE. */
  home?: boolean;
}) {
  const { pathname } = useLocation();
  const name = useBreadcrumbStore((s) => s.names[pathname]);
  const page = menuTrailFor(pathname, TRAILS);
  if (!page) return null;

  const below = pathname
    .slice(page.path.length)
    .split("/")
    .filter((part) => part && !KIND_WORDS.has(part));
  const tail: ReactNode[] = below.map((part, i) => {
    const sub = SUB_PAGES[part];
    if (sub) return sub;
    const id = shortId(decodeURIComponent(part));
    return i === below.length - 1 ? (name ?? id) : id;
  });

  const crumbs: ReactNode[] = [
    ...(home || !page.sase ? [<Link to="/">Home</Link>] : []),
    ...(page.sase ? [<Link to={SASE_HOME}>{SASE_TITLE}</Link>, ...page.groups] : []),
    tail.length ? <Link to={page.path}>{page.name}</Link> : page.name,
    ...tail,
  ];
  return (
    <Breadcrumb
      items={crumbs.map((title, i) => ({ key: i, title }))}
      style={{ margin: "16px 24px 0", fontSize: 13 }}
    />
  );
}

export default Breadcrumbs;
